import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { Capacitor } from "@capacitor/core";
import {
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  signInWithCredential,
  signInWithPopup,
  signOut as fbSignOut,
  type User,
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { auth, db } from "./firebase";
import { seedRecipesIfNeeded, upsertPublicProfile } from "./queries";

type AuthState = {
  user: User | null;
  loading: boolean;
  isNewUser: boolean;
  signInGoogle: () => Promise<void>;
  signInApple: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthCtx = createContext<AuthState | null>(null);

const ignorablePopupErrors = new Set([
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
]);

// Native sign-in sheets report user cancellation via error messages rather
// than the web SDK's auth/* codes.
function isUserCancellation(e: any): boolean {
  if (ignorablePopupErrors.has(e?.code)) return true;
  return /cancel/i.test(String(e?.message ?? ""));
}

const NEW_USER_FLAG = "affinage.auth.justCreated";

async function recordSignIn(user: User) {
  const userRef = doc(db, "users", user.uid);
  let isFirst = false;
  try {
    const snap = await getDoc(userRef);
    isFirst = !snap.exists();
  } catch {
    // If the read fails (rules, offline), fall back to non-first to avoid spurious tours.
    isFirst = false;
  }

  await setDoc(
    userRef,
    {
      email: user.email ?? null,
      displayName: user.displayName ?? null,
      lastSignIn: serverTimestamp(),
      ...(isFirst ? { createdAt: serverTimestamp() } : {}),
    },
    { merge: true }
  );

  if (isFirst) {
    try {
      window.sessionStorage.setItem(NEW_USER_FLAG, "1");
    } catch {
      // ignore
    }
  }

  try {
    await seedRecipesIfNeeded(user.uid);
  } catch (e) {
    console.error("Failed to seed default recipes", e);
  }

  // Publish a minimal, searchable identity (display name only — no email) so
  // friends can find this user to connect and serve recipes.
  try {
    await upsertPublicProfile(user.uid, user.displayName, user.photoURL);
  } catch (e) {
    console.error("Failed to write public profile", e);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (u) {
        try {
          const flag = window.sessionStorage.getItem(NEW_USER_FLAG);
          setIsNewUser(flag === "1");
        } catch {
          setIsNewUser(false);
        }
      } else {
        setIsNewUser(false);
      }
      setLoading(false);
    });
  }, []);

  const finishSignIn = async (user: User) => {
    await recordSignIn(user);
    try {
      setIsNewUser(window.sessionStorage.getItem(NEW_USER_FLAG) === "1");
    } catch {
      // ignore
    }
  };

  const signInGoogle = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        // Popups can't work in the WKWebView — sign in with the native Google
        // sheet, then bridge the credential into the web SDK (which Firestore
        // and the rest of the app authenticate through).
        const result = await FirebaseAuthentication.signInWithGoogle();
        const idToken = result.credential?.idToken;
        if (!idToken) throw new Error("Google sign-in returned no credential");
        const credential = GoogleAuthProvider.credential(
          idToken,
          result.credential?.accessToken
        );
        const { user } = await signInWithCredential(auth, credential);
        await finishSignIn(user);
      } else {
        const { user } = await signInWithPopup(auth, new GoogleAuthProvider());
        await finishSignIn(user);
      }
    } catch (e: any) {
      if (!isUserCancellation(e)) throw e;
    }
  };

  const signInApple = async () => {
    try {
      const provider = new OAuthProvider("apple.com");
      if (Capacitor.isNativePlatform()) {
        // skipNativeAuth: Apple ID tokens are single-use, so the native layer
        // must not consume it — the web SDK signs in with it instead.
        const result = await FirebaseAuthentication.signInWithApple({
          skipNativeAuth: true,
        });
        const idToken = result.credential?.idToken;
        if (!idToken) throw new Error("Apple sign-in returned no credential");
        const credential = provider.credential({
          idToken,
          rawNonce: result.credential?.nonce,
        });
        const { user } = await signInWithCredential(auth, credential);
        await finishSignIn(user);
      } else {
        const { user } = await signInWithPopup(auth, provider);
        await finishSignIn(user);
      }
    } catch (e: any) {
      if (!isUserCancellation(e)) throw e;
    }
  };

  const signOut = async () => {
    try {
      window.sessionStorage.removeItem(NEW_USER_FLAG);
    } catch {
      // ignore
    }
    if (Capacitor.isNativePlatform()) {
      try {
        await FirebaseAuthentication.signOut();
      } catch {
        // Native session cleanup is best-effort; web sign-out still proceeds.
      }
    }
    await fbSignOut(auth);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, isNewUser, signInGoogle, signInApple, signOut }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="empty">
        <span className="spinner" /> &nbsp; Loading your cookbook…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return <>{children}</>;
}
